import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button, message, Input, Grid, Space, Flex, Pagination, Card } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useGlobal } from '../Shared/GlobalContext/GlobalContext';
import { getAgents, createOrUpdateAgent, deleteAgent } from './AgentServices';
import AgentList from './AgentList/AgentList';
import AddAgentModal from './AddAgent/AddAgent';
import { useErrorHandler } from '../../Hooks/useErrorHandler';
import { useTranslation } from 'react-i18next';
import useSocket from '../../Hooks/useSocket';
import useSocketSubscription from '../../Hooks/useSocketSubscription';
import useSocketEvent from '../../Hooks/useSocketEvent';
import { Resource, Action } from '../../Constants/socket';

const { useBreakpoint } = Grid;

const AgentsPage = () => {
    const { t } = useTranslation();
    const screens = useBreakpoint();
    const { setLoading } = useGlobal();
    const [skeleton, setSkeleton] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const { handleError } = useErrorHandler();
    const [agents, setAgents] = useState([]);
    const [metadata, setMetadata] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [modalMode, setModalMode] = useState('create');
    const [isInputFocused, setIsInputFocused] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 25,
        total: 0
    });
    const { connectionState } = useSocket();
    const isSubscribed = useSocketSubscription(Resource.AGENT);
    const agentIds = useMemo(() => agents.map(agent => agent.id), [agents]);
    useSocketSubscription(Resource.AGENT, agentIds);

    const fetchAgents = useCallback(async (page = 1, search = '', pageSize) => {
        try {
            setLoading(true);
            const { data, metadata } = await getAgents({
                page,
                size: pageSize,
                search: search.trim()
            });
            setPagination({
                current: metadata.page,
                pageSize: metadata.per_page,
                total: metadata.total
            });
            setAgents(data);
            setMetadata(metadata);
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
            setSkeleton(false);
        }
    }, [setLoading, handleError]);

    // Handle new agents
    const handleAgentCreated = useCallback((data) => {
        fetchAgents(currentPage, searchText);
    }, [currentPage, searchText, fetchAgents]);

    // Handle updated agents
    const handleAgentUpdated = useCallback((data) => {
        setAgents(prev => 
            prev.map(agent => agent.id === data.id ? data : agent)
        );
    }, []);

    // Handle deleted agents
    const handleAgentDeleted = useCallback((data) => {
        fetchAgents(currentPage, searchText);
    }, [currentPage, searchText, fetchAgents]);

    // Register socket event listeners
    useSocketEvent(Resource.AGENT, Action.CREATE, handleAgentCreated);
    useSocketEvent(Resource.AGENT, Action.UPDATE, handleAgentUpdated);
    useSocketEvent(Resource.AGENT, Action.DELETE, handleAgentDeleted);

    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize
        }));
    };

    const debouncedSearch = useCallback(
        debounce((searchValue) => {
            setCurrentPage(1);
            fetchAgents(1, searchValue, pagination.pageSize);
        }, 500),
        [fetchAgents]
    );

    useEffect(() => {
        fetchAgents(currentPage, searchText, pagination.pageSize);
    }, [currentPage, fetchAgents, searchText, pagination.pageSize]);

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchText(value);
        debouncedSearch(value);
    }, [debouncedSearch]);

    const handleAgentCreate = useCallback(async (agentData, finish) => {
        try {
            setLoading(true);
            await createOrUpdateAgent(agentData);
            message.success(t(modalMode === 'edit' ? 'agent_updated_successfully' : 'agent_created_successfully'));
            fetchAgents(currentPage, searchText);
            setIsModalVisible(false);
            setSelectedAgent(null);
            setModalMode('create');
        } catch (error) {
            handleError(error);
        } finally {
            setLoading(false);
            finish();
        }
    }, [currentPage,
        searchText, fetchAgents,
        setIsModalVisible, setSelectedAgent,
        setModalMode, setLoading,
        modalMode, handleError, t
    ]);

    const handleDelete = useCallback(async (agentId) => {
        try {
            setLoading(true);
            await deleteAgent(agentId);
            message.success(t('agent_deleted_successfully'));
            fetchAgents(currentPage, searchText);
        } catch (error) {
            console.error('Error deleting agent:', error);
            message.error(t('failed_to_delete_agent'));
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchText, fetchAgents, setLoading, t]);

    const handleStatusChange = useCallback(async (agent, id, onStatusChange) => {
        try {
            onStatusChange(true);
            const result = await createOrUpdateAgent(agent, id);

            if (result.success) {
                fetchAgents(currentPage, searchText);
                message.success(t(agent.status === 'started' ? 'agent_started_successfully' : 'agent_stopped_successfully'));
            }
        } catch (error) {
            message.error(error.response?.data?.message || t('failed_to_update_agent_status'));
        } finally {
            onStatusChange(false);
        }
    }, [fetchAgents, currentPage, searchText, t]);

    const handleSettings = useCallback((agent) => {
        setSelectedAgent(agent);
        setModalMode('edit');
        setIsModalVisible(true);
    }, []);

    const handleClose = () => {
        setIsModalVisible(false);
        setSelectedAgent(null);
        setModalMode('create');
    };

    if (!connectionState.status === 'connected') {
        return <div>Socket disconnected. Trying to reconnect...</div>;
    }

    return (
        <Flex
            vertical
            style={{
                flexGrow: 1,
                padding: 0
            }}
        >

            <Card style={{
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            }}
                title={t('agents')}
                extra={
                    <Button
                        type="link"
                        icon={<PlusOutlined />}
                        size="medium"
                        onClick={() => setIsModalVisible(true)}
                    >
                        {t('add_agent')}
                    </Button>
                }
                styles={{
                    body: {
                        padding: 16
                    }
                }}
            >
                <Flex
                    gap="middle"
                    wrap="wrap"
                >
                    <Flex
                        flex={{ xs: '100%', md: 1 }}
                        style={{ flexGrow: 1 }}
                    >
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder={t('search_agents')}
                            size="medium"
                            onChange={handleSearchChange}
                            onFocus={() => setIsInputFocused(true)}
                            onBlur={() => setIsInputFocused(false)}
                            value={searchText}
                            style={{ 
                                width: isInputFocused ? '100%' : (screens.xs ? '100%' : '400px'),
                                transition: 'width 0.4s ease-in-out'
                            }}
                        />
                    </Flex>
                </Flex>
            </Card >

            <Space direction="vertical" style={{ width: '100%', marginTop: 16, flex: 1 }}>
                <AgentList
                    agents={agents}
                    onStatusChange={handleStatusChange}
                    onSettings={handleSettings}
                    onDelete={handleDelete}
                    skeleton={skeleton}
                />
            </Space>

            <Flex justify="center" style={{ marginTop: 16 }}>
                <Pagination
                    current={pagination.current}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={handlePageChange}
                    showSizeChanger
                    pageSizeOptions={[10, 25, 50, 100]}
                    showTotal={(total, range) =>
                        `${range[0]}-${range[1]} of ${total} items`
                    }
                />
            </Flex>
            {
                isModalVisible && <AddAgentModal
                    onClose={handleClose}
                    onCreateAgent={handleAgentCreate}
                    mode={modalMode}
                    initialData={selectedAgent}
                />
            }
        </Flex >
    );
};

export default AgentsPage;
